import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Telemetry: ships three things wired off /api/config/public:
 *   1. GA4 (Google Analytics 4) — injected via gtag script + SPA pageview hook
 *   2. Google Search Console / Bing site verification meta tags
 *   3. Anonymous internal pageview tracking → POST /api/track/pageview
 *
 * Privacy:
 *   - We DO NOT initialise GA4 until the visitor accepts cookies (banner).
 *   - Internal tracking is anonymous (random session UUID, no PII).
 *   - Internal tracking respects ?do_not_track=1 and HPM_TELEMETRY=0.
 *
 * Mount once at the top of <App />.
 */
const SESSION_KEY = "hpm_anon_session";
const CONSENT_KEY = "hpm_ga_consent"; // "granted" | "denied" | null

function getOrCreateSession() {
    let id = sessionStorage.getItem(SESSION_KEY);
    let isNew = false;
    if (!id) {
        id = (crypto.randomUUID && crypto.randomUUID()) || Math.random().toString(36).slice(2);
        sessionStorage.setItem(SESSION_KEY, id);
        isNew = true;
    }
    return { id, isNew };
}

function injectMetaTag(name, content) {
    if (!content) return;
    if (document.querySelector(`meta[name="${name}"]`)) return;
    const m = document.createElement("meta");
    m.setAttribute("name", name);
    m.setAttribute("content", content);
    document.head.appendChild(m);
}

function injectGA4(measurementId) {
    if (!measurementId) return;
    if (window.__hpm_ga4_loaded) return;
    window.__hpm_ga4_loaded = true;
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
    };
    // Start with consent denied to be GDPR-friendly; flip after user grants.
    window.gtag("consent", "default", {
        analytics_storage: "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
    });
    window.gtag("js", new Date());
    window.gtag("config", measurementId, { send_page_view: false });
}

function gaPageview(path) {
    if (!window.gtag || !window.__hpm_ga4_id) return;
    window.gtag("event", "page_view", {
        page_path: path,
        page_location: window.location.href,
        page_title: document.title,
    });
}

export default function Telemetry() {
    const location = useLocation();
    const [config, setConfig] = useState(null);
    const [consent, setConsent] = useState(() => localStorage.getItem(CONSENT_KEY));

    // Pull config once
    useEffect(() => {
        axios
            .get(`${API}/config/public`)
            .then((r) => setConfig(r.data))
            .catch(() => setConfig(null));
    }, []);

    // Inject GA4 + meta tags once config arrives
    useEffect(() => {
        if (!config) return;
        injectMetaTag("google-site-verification", config.google_site_verification);
        injectMetaTag("msvalidate.01", config.bing_site_verification);
        if (config.ga4_measurement_id) {
            injectGA4(config.ga4_measurement_id);
            window.__hpm_ga4_id = config.ga4_measurement_id;
            if (consent === "granted" && window.gtag) {
                window.gtag("consent", "update", {
                    analytics_storage: "granted",
                });
            }
        }
    }, [config, consent]);

    // SPA pageview tracking on every route change
    useEffect(() => {
        if (!config || !config.telemetry_enabled) return;
        const url = new URL(window.location.href);
        if (url.searchParams.get("do_not_track") === "1") return;

        // Internal anonymous beacon
        const { id, isNew } = getOrCreateSession();
        axios
            .post(`${API}/track/pageview`, {
                path: location.pathname,
                referrer: document.referrer || "",
                session_id: id,
                lang: navigator.language || "",
                is_new_session: isNew,
            })
            .catch(() => {});

        // GA4 SPA pageview
        gaPageview(location.pathname + location.search);
    }, [location.pathname, location.search, config]);

    const grant = () => {
        localStorage.setItem(CONSENT_KEY, "granted");
        setConsent("granted");
    };
    const deny = () => {
        localStorage.setItem(CONSENT_KEY, "denied");
        setConsent("denied");
    };

    // Cookie banner: only shown if GA4 is configured AND consent not yet decided
    const showBanner = !!config?.ga4_measurement_id && !consent;

    if (!showBanner) return null;

    return (
        <div
            data-testid="cookie-consent-banner"
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 border-2 border-white/20 bg-[#0a0a0a]/95 backdrop-blur-sm p-5 shadow-2xl"
        >
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-2">
                COOKIES / ANALYTICS
            </div>
            <p className="font-sans text-sm text-white/80 leading-relaxed mb-4">
                HPM uses Google Analytics to understand which justice content
                actually reaches people. No ads, no resale of data. You can refuse
                and the site works exactly the same. See our{" "}
                <a className="text-[#FF3B30] underline" href="/privacy">Privacy Policy</a>.
            </p>
            <div className="flex gap-3">
                <button
                    onClick={grant}
                    className="btn-brutal btn-brutal-red text-[10px]"
                    data-testid="cookie-consent-accept"
                >
                    ACCEPT
                </button>
                <button
                    onClick={deny}
                    className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 hover:text-white underline decoration-dotted underline-offset-4"
                    data-testid="cookie-consent-decline"
                >
                    NO THANKS
                </button>
            </div>
        </div>
    );
}
